import useSWR from 'swr';
import axios from 'axios';
import { bacchusAPIEndpoint } from '../config';
import { ApiHelpers } from '../helpers';
import moment from 'moment';

const fetcher = (url) => axios.get(url).then((res) => res.data);

function useRestaurantById(id) {
    const { data, error } = useSWR(bacchusAPIEndpoint + '/admin/restaurant/' + id, fetcher);
    return {
        data,
        isLoading: !error && !data,
        isError: error,
    };
}

function useRestaurantDetailsById(id) {
    const { data, error } = useSWR(id ? bacchusAPIEndpoint + '/admin/restaurant/' + id : null, fetcher, {
        revalidateIfStale: false,
        revalidateOnFocus: false,
        revalidateOnReconnect: false,
    });
    return {
        data,
        isLoading: !error && !data,
        isError: error,
    };
}

function useRestaurantDetailsPublicById(id) {
    const { data, error } = useSWR(bacchusAPIEndpoint + '/api/menu/' + id, fetcher);
    return {
        data,
        isLoading: !error && !data,
        isError: error,
    };
}

function useRestaurant(id) {
    const { data: company } = useSWR(id ? bacchusAPIEndpoint + '/admin/company/getByUuid/' + id : null, fetcher, {
        onErrorRetry: (error, key, config, revalidate, { retryCount }) => {
            // Only retry up to 10 times.
            if (retryCount >= 3) return;

            // Retry after 5 seconds.
            setTimeout(() => revalidate({ retryCount }), 5000);
        },
        shouldRetryOnError: false,
    });
    const { data, error } = useSWR(() => bacchusAPIEndpoint + '/admin/restaurant/getByCompany/' + company.uniqueIdentificationToken, fetcher);
    return {
        data,
        isLoading: !error && !data,
        isError: error,
    };
}

function useMenusByRestaurant(uniqueIdentificationToken) {
    const { data, error } = useSWR(
        uniqueIdentificationToken ? bacchusAPIEndpoint + '/admin/menu/getByRestaurant/' + uniqueIdentificationToken : null,
        fetcher
    );
    return {
        data: data,
        isLoading: !error && !data,
        isError: error,
    };
}

function useMenuById(restaurantIdentificationToken, uniqueIdentificationToken) {
    const { data, error } = useSWR(
        uniqueIdentificationToken
            ? bacchusAPIEndpoint + '/admin/menu/getByUniqueCode/' + restaurantIdentificationToken + '/' + uniqueIdentificationToken
            : null,
        fetcher
    );
    return {
        data,
        isLoading: !error && !data,
        isError: error,
    };
}

// Restaurant Menu items
function useMenuItemsByRestaurant(uniqueIdentificationToken) {
    const { data, error } = useSWR(
        uniqueIdentificationToken ? bacchusAPIEndpoint + '/admin/menu/getMenuItemsByRestaurant/' + uniqueIdentificationToken : null,
        fetcher
    );
    return {
        data,
        isLoading: !error && !data,
        isError: error,
    };
}

// Restaurant Tables
function useTablesByRestaurant(uniqueIdentificationToken) {
    const { data, error } = useSWR(
        uniqueIdentificationToken ? bacchusAPIEndpoint + '/admin/restaurant/table/getRestaurantTables/' + uniqueIdentificationToken : null,
        fetcher
    );
    return {
        data,
        isLoading: !error && !data,
        isError: error,
    };
}

// Restaurant events
function useEventsByRestaurant(uniqueIdentificationToken, activeOnly = false) {
    const { data, error, mutate } = useSWR(bacchusAPIEndpoint + '/admin/restaurant/getEvents/' + uniqueIdentificationToken + '/' + activeOnly, fetcher);
    return {
        data,
        isLoading: !error && !data,
        isError: error,
        mutate
    };
}

// Restaurant offers / promotions
function usePromotionsByRestaurant(uniqueIdentificationToken, activeOnly = false) {
    const { data, error, mutate } = useSWR(bacchusAPIEndpoint + '/admin/restaurant/getPromotions/' + uniqueIdentificationToken + '/' + activeOnly, fetcher);
    return {
        data,
        isLoading: !error && !data,
        isError: error,
        mutate
    };
}

// Restaurant Bookings - COMPLETELY NEW IMPLEMENTATION
function useBookingsByRestaurant(restaurantToken, options = {}) {
    const { refreshInterval = 30000, ...otherOptions } = options;

    // Validate restaurant token
    const isValidToken = restaurantToken && 
                        typeof restaurantToken === 'string' && 
                        restaurantToken.length > 5 && 
                        restaurantToken !== 'o';

 

    // Simple fetcher using closure over restaurantToken
    const bookingsFetcher = async () => {

        
        try {
            const response = await ApiHelpers.getBookingsByRestaurant(restaurantToken);
            return response.data || [];
        } catch (error) {
            throw error;
        }
    };

    // Use simple string key
    const swrKey = isValidToken ? `bookings-${restaurantToken}` : null;

    const { data, error, mutate } = useSWR(
        swrKey,
        bookingsFetcher, // Uses restaurantToken from closure
        {
            refreshInterval: isValidToken ? refreshInterval : 0,
            revalidateOnFocus: false,
            revalidateOnReconnect: false,
            errorRetryCount: 1,
            errorRetryInterval: 5000,
            shouldRetryOnError: (error) => {
                const shouldRetry = !(error.response?.status >= 400 && error.response?.status < 500);
                return shouldRetry;
            },
            onError: (error) => {
                console.error('SWR onError:', error.message);
            },
            onSuccess: (data) => {
            },
            ...otherOptions
        }
    );

    // Transform data for calendar view
    const transformedData = data ? data.map(booking => {
        const dateStr = booking.reservationDate ? booking.reservationDate.split('T')[0] : '';
        const hourStart = booking.hourStart ? booking.hourStart.split(':').slice(0, 2).join(':') : '';
        const hourEnd = booking.hourEnd ? booking.hourEnd.split(':').slice(0, 2).join(':') : '';

        const startDateTime = moment(`${dateStr}T${hourStart}`).toDate();
        const endDateTime = moment(`${dateStr}T${hourEnd}`).toDate();

        return {
            ...booking,
            formattedDate: dateStr,
            formattedHourStart: hourStart,
            formattedHourEnd: hourEnd,
            start: startDateTime,
            end: endDateTime,
            title: `${booking.customerName} - ${booking.partySize} persoane`
        };
    }) : [];

    return {
        bookings: transformedData,
        rawData: data,
        isLoading: !error && !data && isValidToken,
        isError: !!error,
        mutate,
        refresh: () => mutate(),
        hasValidToken: isValidToken
    };
}

export {
    useRestaurant,
    useRestaurantById,
    useMenusByRestaurant,
    useRestaurantDetailsPublicById,
    useRestaurantDetailsById,
    useMenuItemsByRestaurant,
    useTablesByRestaurant,
    useEventsByRestaurant,
    usePromotionsByRestaurant,
    useMenuById,
    useBookingsByRestaurant,
};